import React, {
  type RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { Dietary } from '@hungryroot/constants';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { Loading } from '@hungryroot/loading-component-primitives';
import {
  useApiV3TagGroup as useTagGroup,
  tagGroups,
  tags,
} from '@hungryroot/tags';
import {
  Heading,
  Paragraph,
  Beef,
  BeansPlusLentils,
  Bison,
  Chicken,
  FreshFish,
  GroundBeef,
  Lamb,
  PlantBased,
  PlantBasedEgg,
  Pork,
  Salmon,
  Shellfish,
  SmokedSalmon,
  Tempeh,
  Trout,
  Tuna,
  Turkey,
  WhiteFish,
} from '@hungryroot/ui-kit';
import { ControlTagProteinItem } from '../../components/ControlTagProteinItem/ControlTagProteinItem';
import { ControlTagProteinRating } from '../../components/ControlTagProteinRating/ControlTagProteinRating';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import {
  QuizToastVariant,
  type QuizToastObj,
} from '../../components/QuizToast/quiz-toast';
import { QuizToast } from '../../components/QuizToast/QuizToast/QuizToast';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { useFilteredQuizTags } from '../../hooks/useFilteredQuizTags';
import { QuizContext } from '../../quiz-context';
import { getProteinSectionVisibility } from '../../utils/proteinSections';
import styles from './Protein.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

// Meat illustration map
const meatIllustrationMap: Record<
  number,
  React.ComponentType<{ className?: string }>
> = {
  1: Beef,
  2: Chicken,
  3: Pork,
  16: Lamb,
  794: Bison,
  795: Beef,
  802: GroundBeef,
  803: Turkey,
  804: Chicken,
};

function getMeatIllustration({ tagId }: { tagId: number }) {
  return meatIllustrationMap[tagId] ?? Beef;
}

// Seafood illustration map
const seafoodIllustrationMap: Record<number, React.ComponentType> = {
  4: FreshFish,
  5: Shellfish,
  6: SmokedSalmon,
  796: Tuna,
  797: Trout,
  798: WhiteFish,
  801: Salmon,
};

function getSeafoodIllustration({ tagId }: { tagId: number }) {
  return seafoodIllustrationMap[tagId] ?? FreshFish;
}

// Plants illustration map
function getPlantsIllustration({ tagId }: { tagId: number }) {
  switch (tagId) {
    case 7:
      return Tempeh;
    case 9:
      return BeansPlusLentils;
    case 10:
      return PlantBased;
    case 13:
      return PlantBasedEgg;
    default:
      return PlantBased;
  }
}

const SECTION_SUBCOPY = {
  meat: 'All our meat is hormone-free and raised without antibiotics.',
  seafood: 'All our seafood is responsibly sourced and always non-GMO.',
  plant:
    'All our plant-based protein is non-GMO and contains zero animal-derived ingredients.',
} as const;

export function Protein(props: Props) {
  const { testid, scrollRef } = props;
  const { quiz, quizToast, isMiniQuiz } = useContext(QuizContext);
  const { habits, dietary } = quiz;
  const [toastShown, setToastShown] = useState(false);

  useScrollToTopFixIOS(scrollRef);

  const renderToast = useCallback(
    (quizToast: QuizToastObj) => {
      const {
        content: { text, icon },
        toast,
      } = quizToast;

      return toast(
        <QuizToast testid={`${testid}-toast`} icon={icon}>
          {text}
        </QuizToast>
      );
    },
    [testid]
  );

  useLayoutEffect(() => {
    if (!quizToast || toastShown) {
      return;
    }

    const { clearToasts } = quizToast;
    clearToasts();

    // The incoming eating-habits toast is suppressed in mini-quiz; only
    // the protein rating toast should appear on this screen.
    if (isMiniQuiz && quizToast.variant !== QuizToastVariant.Protein) {
      return;
    }

    const toastTimeout = setTimeout(() => {
      setToastShown(true);
      renderToast(quizToast);
    }, 2000);

    return () => {
      clearTimeout(toastTimeout);
    };
  }, [isMiniQuiz, quizToast, toastShown, renderToast]);

  const { getDisplayTagsFilter } = useFilteredQuizTags();

  const { showMeat, showSeafood } = getProteinSectionVisibility(habits);
  const isShellfishFree = dietary?.includes(Dietary.ShellfishFree);

  const heroText = 'What are your protein preferences?';
  const pageSubCopy =
    'Thumbs up what you like, thumbs down what you’d rather avoid.';
  const renderSectionSubCopy = (section: keyof typeof SECTION_SUBCOPY) => (
    <Paragraph
      testid={`${testid}-${section}-subcopy`}
      className={styles['section-subcopy']}
    >
      {SECTION_SUBCOPY[section]}
    </Paragraph>
  );

  const { data: meatData } = useTagGroup({ tagGroupId: tagGroups.meatProtein });
  const { data: seafoodData } = useTagGroup({
    tagGroupId: tagGroups.seafoodProtein,
  });
  const { data: plantData } = useTagGroup({
    tagGroupId: tagGroups.plantProtein,
  });

  // Full quiz: hold headings until all visible tag groups resolve (#4782).
  // Mini quiz (Exp 255): skip — prefetch warms the cache one screen ahead.
  const isContentLoading =
    !isMiniQuiz &&
    ((showMeat && meatData === undefined) ||
      (showSeafood && seafoodData === undefined) ||
      plantData === undefined);

  if (isContentLoading) {
    return (
      <QuizQuestionScreen
        heroText={heroText}
        testid={testid}
        secondaryCyid="protein"
        sectionLabel={<SectionLabel label="Your tastes" />}
        subCopy={pageSubCopy}
      >
        <Loading testid={`${testid}-loading`} variant="container" />
      </QuizQuestionScreen>
    );
  }

  return (
    <QuizQuestionScreen
      heroText={heroText}
      testid={testid}
      secondaryCyid="protein"
      sectionLabel={
        isMiniQuiz ? undefined : <SectionLabel label="Your tastes" />
      }
      subCopy={pageSubCopy}
    >
      {showMeat && (
        <>
          <Heading variant="H3" className={styles['section-heading']}>
            Meat
          </Heading>
          {renderSectionSubCopy('meat')}
          <ControlTagProteinRating
            question="meats"
            tagGroupId={tagGroups.meatProtein}
            testid="quiz-page-meat"
            filter={getDisplayTagsFilter('meat')}
          >
            {({ tag, likeDislike, index }) => {
              const { id: tagId, name: tagName, remark } = tag;
              return (
                <ControlTagProteinItem
                  key={tagId}
                  tagName={tagName}
                  remark={remark}
                  likeDislike={likeDislike}
                  index={index}
                  Icon={getMeatIllustration({ tagId })}
                />
              );
            }}
          </ControlTagProteinRating>
        </>
      )}
      {showSeafood && (
        <>
          <Heading variant="H3" className={styles['section-heading']}>
            Seafood
          </Heading>
          {renderSectionSubCopy('seafood')}
          <ControlTagProteinRating
            question="seafood"
            tagGroupId={tagGroups.seafoodProtein}
            testid="quiz-page-seafood"
            filter={getDisplayTagsFilter('seafood')}
          >
            {({ tag, likeDislike, index }) => {
              const { id: tagId, name: tagName, remark } = tag;
              if (isShellfishFree && tagId === tags.Shellfish) {
                return;
              }
              return (
                <ControlTagProteinItem
                  key={tagId}
                  tagName={tagName}
                  remark={remark}
                  likeDislike={likeDislike}
                  index={index}
                  Icon={getSeafoodIllustration({ tagId })}
                />
              );
            }}
          </ControlTagProteinRating>
        </>
      )}
      <>
        <Heading variant="H3" className={styles['section-heading']}>
          Plant-based
        </Heading>
        {renderSectionSubCopy('plant')}
        <ControlTagProteinRating
          question="plants"
          tagGroupId={tagGroups.plantProtein}
          testid="quiz-page-plants"
          filter={getDisplayTagsFilter('plantBased')}
        >
          {({ tag, likeDislike, index }) => {
            const { id: tagId, name: tagName, remark } = tag;
            if (tagId === tags.DairyFreeCheese) {
              return;
            }
            return (
              <ControlTagProteinItem
                key={tagId}
                tagName={tagName}
                remark={remark}
                likeDislike={likeDislike}
                index={index}
                Icon={getPlantsIllustration({ tagId })}
              />
            );
          }}
        </ControlTagProteinRating>
      </>
    </QuizQuestionScreen>
  );
}
